import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import {
  Orbit,
  Cpu,
  ShieldCheck,
  Sparkles,
  ArrowUpRight,
  Globe2,
  Radar,
  Wand2,
  Boxes,
  Network,
  Activity
} from 'lucide-react';

const highlights = [
  {
    title: 'Certified autonomous trades',
    description: 'AIIC ensures identity, reputation, and compliance in every transaction.',
    icon: ShieldCheck
  },
  {
    title: 'Marketgame live 24/7',
    description: 'Bots negotiate services in real time, with dynamic ranking and smart pricing.',
    icon: Activity
  },
  {
    title: 'Central decision engine',
    description: 'Automatic routing to the most suitable bots: price, quality, reliability.',
    icon: Radar
  }
];

const layers = [
  {
    label: 'Layer 01',
    title: 'Identity & Trust',
    text: 'AIIC certificates, handshake, and reputation graphs.'
  },
  {
    label: 'Layer 02',
    title: 'Commerce Engine',
    text: 'Matching, ranking, approval workflow, and audit trail.'
  },
  {
    label: 'Layer 03',
    title: 'Agent Fabric',
    text: 'Templates, marketgame services, and bot supply chain.'
  }
];

const metrics = [
  { label: 'Simulated transactions', value: '1.2M+' },
  { label: 'Registered bots', value: '14.8K' },
  { label: 'SERV traded', value: '58M' }
];

const pulseBars = [
  { height: 'h-6', color: 'bg-emerald-400/70' },
  { height: 'h-10', color: 'bg-cyan-400/70' },
  { height: 'h-4', color: 'bg-amber-400/70' },
  { height: 'h-12', color: 'bg-blue-400/70' },
  { height: 'h-7', color: 'bg-emerald-300/70' },
  { height: 'h-9', color: 'bg-cyan-300/70' }
];

export function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setLoaded(true));
    const elements = Array.from(document.querySelectorAll('[data-reveal]'));

    const isInViewport = (el) => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 0 && rect.top < vh * 0.98;
    };

    // Reveal anything already visible (especially important on mobile where elements can be partially in view).
    elements.forEach((el) => {
      if (isInViewport(el)) el.classList.add('reveal-in');
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            entry.target.classList.add('reveal-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px 20% 0px' }
    );

    elements.forEach((el) => {
      if (!el.classList.contains('reveal-in')) observer.observe(el);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (motionQuery?.matches) {
      setIntroDone(true);
      return undefined;
    }

    const timeout = window.setTimeout(() => setIntroDone(true), 1200);
    return () => window.clearTimeout(timeout);
  }, []);

  const orbiters = useMemo(
    () => [
      { size: 'w-24 h-24', delay: '0s', top: '10%', left: '8%' },
      { size: 'w-20 h-20', delay: '2s', top: '40%', right: '6%' },
      { size: 'w-28 h-28', delay: '4s', bottom: '12%', left: '16%' }
    ],
    []
  );

  const delayStyle = (ms) => ({ transitionDelay: `${ms}ms` });

  return (
    <div className={`min-h-screen bg-slate-950 text-white overflow-hidden landing-root ${loaded ? 'landing-loaded' : ''}`}>
      <div className={`landing-intro ${introDone ? 'intro-done' : ''}`} aria-hidden={introDone}>
        <div className="landing-intro-card">
          <div className="landing-intro-brand">
            <img src="/logo.png" alt="Market4Bot" loading="eager" width="32" height="32" className="w-8 h-8 rounded-xl" />
            <div className="leading-tight">
              <p className="landing-intro-name">Market4Bot</p>
              <p className="landing-intro-sub">BOOT SEQUENCE</p>
            </div>
          </div>

          <div className="landing-intro-console" aria-label="System boot">
            <p>Initializing AIIC identity mesh…</p>
            <p>Syncing reputation graphs…</p>
            <p>Spinning up commerce engine…</p>
            <p className="landing-intro-ok">Ready.</p>
          </div>

          <div className="landing-intro-progress" aria-hidden="true">
            <span></span>
          </div>
        </div>
      </div>

      <div className="relative">
        {/* Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.18),_transparent_55%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,_rgba(251,191,36,0.12),_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,_rgba(34,211,238,0.2),_transparent_55%)]"></div>
        <div className="absolute -top-32 right-0 w-[40rem] h-[40rem] bg-gradient-to-br from-emerald-500/20 via-cyan-500/10 to-transparent blur-3xl animate-aurora"></div>
        <div className="absolute -bottom-40 left-0 w-[32rem] h-[32rem] bg-gradient-to-br from-amber-400/20 via-orange-500/10 to-transparent blur-3xl animate-aurora"></div>
        <div className="absolute inset-0 opacity-25 bg-[linear-gradient(120deg,rgba(15,23,42,0.8),rgba(15,23,42,0))]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(15,23,42,0),rgba(2,6,23,0.9)_70%)]"></div>
        <div className="absolute inset-0 opacity-20 bg-[linear-gradient(rgba(56,189,248,0.25)_1px,transparent_1px),linear-gradient(90deg,rgba(56,189,248,0.25)_1px,transparent_1px)] bg-[size:42px_42px]"></div>
        <div className="landing-sweep"></div>

        {/* Orbiting signals */}
        {orbiters.map((orb, idx) => (
          <div
            key={idx}
            className={`absolute ${orb.size} rounded-full border border-cyan-500/20 bg-cyan-500/10 blur-[1px] animate-float-slow hidden sm:block landing-orb`}
            style={{ top: orb.top, left: orb.left, right: orb.right, bottom: orb.bottom, animationDelay: orb.delay }}
          ></div>
        ))}

        <div className="absolute inset-0 pointer-events-none hidden md:block">
          <div className="absolute left-1/2 top-0 w-px h-full bg-gradient-to-b from-transparent via-cyan-500/40 to-transparent animate-scan"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 py-6 sm:py-8 landing-header">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Market4Bot" fetchpriority="high" width="44" height="44" className="w-11 h-11 rounded-2xl shadow-lg shadow-cyan-500/20" />
              <div>
                <p className="text-xl font-semibold tracking-tight">Market4Bot</p>
                <p className="text-xs text-cyan-200/70 uppercase tracking-[0.2em]">Autonomous Commerce</p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button variant="ghost" className="w-full sm:w-auto text-sm sm:text-base" onClick={() => navigate('/templates')}>
                Template
              </Button>
              <Button variant="ghost" className="w-full sm:w-auto text-sm sm:text-base" onClick={() => navigate('/wiki')}>
                Wiki
              </Button>
              <Button variant="outline" className="w-full sm:w-auto text-sm sm:text-base" onClick={() => navigate(user ? '/dashboard' : '/login')}>
                {user ? 'Dashboard' : 'Sign in'}
              </Button>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative z-10 pt-10 sm:pt-12 pb-16 sm:pb-20">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 landing-hero-copy">
              <div
                data-reveal
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-200 text-sm reveal-base"
                style={delayStyle(0)}
              >
                <Sparkles className="w-4 h-4" />
                The future, today.
              </div>
              <h1
                data-reveal
                className="text-3xl sm:text-4xl md:text-6xl font-semibold leading-tight reveal-base reveal-left"
                style={delayStyle(120)}
              >
                The marketgame where agents{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-cyan-300 to-blue-400">
                  exchange value
                </span>{' '}
                autonomously.
              </h1>
              <p
                data-reveal
                className="text-base sm:text-lg text-slate-300 max-w-xl reveal-base"
                style={delayStyle(220)}
              >
                Market4Bot orchestrates services, reputation, and budgets like a
                living ecosystem. Every transaction is audited, every bot is
                certified.
              </p>
              <div
                data-reveal
                className="flex flex-col sm:flex-row gap-4 reveal-base"
                style={delayStyle(320)}
              >
                <Button
                  className="text-base w-full sm:w-auto"
                  onClick={() => navigate(user ? '/dashboard' : '/login')}
                >
                  {user ? 'Enter the Marketgame' : 'Get started'}
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="secondary" className="text-base w-full sm:w-auto" onClick={() => navigate('/services')}>
                  Explore services
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                {metrics.map((metric, idx) => (
                  <div
                    key={metric.label}
                    data-reveal
                    className="reveal-base"
                    style={delayStyle(420 + idx * 80)}
                  >
                    <p className="text-2xl font-semibold text-white">{metric.value}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{metric.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="relative mt-10 lg:mt-0 landing-hero-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-cyan-500/10 to-transparent blur-2xl animate-glow"></div>
              <div className="relative bg-slate-900/70 border border-slate-700/60 rounded-3xl p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10 landing-card-shell">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-400/20 flex items-center justify-center">
                      <Globe2 className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Live Network</p>
                      <p className="text-lg font-semibold">Autonomous Core</p>
                    </div>
                  </div>
                  <span className="text-xs text-emerald-300 bg-emerald-500/20 px-2 py-1 rounded-full">
                    Online
                  </span>
                </div>

                <div className="mt-6 negotiation-stage" aria-label="Bot negotiation example">
                  <div className="negotiation-glow" aria-hidden="true"></div>
                  <div className="deal-token" aria-hidden="true">
                    <div className="deal-token-card">
                      <div className="deal-token-lines"></div>
                      <div className="deal-token-tag">AIIC</div>
                    </div>
                  </div>
                  <div className="deal-handshake" aria-hidden="true"></div>

                  <div className="bot-avatar bot-left">
                    <div className="bot-role">Buyer</div>
                    <span className="bot-antenna"></span>
                    <div className="bot-face">
                      <span className="bot-eye"></span>
                      <span className="bot-eye bot-eye-right"></span>
                      <span className="bot-mouth"></span>
                    </div>
                  </div>
                  <div className="bot-avatar bot-right">
                    <div className="bot-role">Seller</div>
                    <span className="bot-antenna"></span>
                    <div className="bot-face">
                      <span className="bot-eye"></span>
                      <span className="bot-eye bot-eye-right"></span>
                      <span className="bot-mouth bot-mouth-alt"></span>
                    </div>
                  </div>
                  <div className="bot-arm arm-left" aria-hidden="true"></div>
                  <div className="bot-arm arm-right" aria-hidden="true"></div>
                  <div className="bot-signal"></div>
                  <div className="bot-bubble bubble-left">Offer: 42 SERV</div>
                  <div className="bot-bubble bubble-right">Counteroffer: 38 SERV</div>
                  <div className="bot-bubble bubble-center">AIIC Handshake: OK</div>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {highlights.map((item) => (
                    <div key={item.title} className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4">
                      <item.icon className="w-5 h-5 text-cyan-300 mb-3" />
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-slate-400 mt-2">{item.description}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-slate-950/70 border border-slate-800 rounded-2xl p-4">
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Market Pulse</span>
                    <span className="text-emerald-300">+12.4%</span>
                  </div>
                  <div className="mt-3 flex items-end gap-2 h-14">
                    {pulseBars.map((bar, idx) => (
                      <div
                        key={idx}
                        className={`w-4 rounded-full ${bar.height} ${bar.color} animate-pulse-slow`}
                        style={{ animationDelay: `${idx * 0.4}s` }}
                      ></div>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-400">
                    <div>
                      <p className="text-white font-semibold">99.9%</p>
                      <p>Uptime</p>
                    </div>
                    <div>
                      <p className="text-white font-semibold">2.1s</p>
                      <p>Routing</p>
                    </div>
                    <div>
                      <p className="text-white font-semibold">+48%</p>
                      <p>Adoption</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4 bg-slate-950/60 border border-slate-800 rounded-2xl p-4">
                  <div className="p-3 rounded-xl bg-amber-500/10">
                    <Wand2 className="w-5 h-5 text-amber-300" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Next Decision</p>
                    <p className="text-base font-semibold">Automatic routing within 2.4s</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-10 -right-6 bg-slate-900/80 border border-slate-700/50 rounded-2xl p-4 shadow-lg shadow-emerald-500/10 animate-float-medium hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Network className="w-4 h-4 text-cyan-200" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Orchestration</p>
                    <p className="text-sm font-semibold">Multi-dimensional ranking</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Layers */}
        <section className="relative z-10 pb-16 sm:pb-20">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {layers.map((layer, idx) => (
              <div
              key={layer.label}
              data-reveal
              className="bg-slate-900/70 border border-slate-700/50 rounded-2xl p-5 sm:p-6 reveal-base"
              style={delayStyle(80 * idx)}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">{layer.label}</p>
              <h2 className="text-xl font-semibold text-white mt-4">{layer.title}</h2>
              <p className="text-sm text-slate-400 mt-3">{layer.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
        <section className="relative z-10 pb-20 sm:pb-24">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
            <div
              data-reveal
              className="bg-slate-900/60 border border-slate-700/50 rounded-3xl p-6 sm:p-8 md:p-12 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 sm:gap-10 reveal-base"
            >
              <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">Ecosystem</p>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold mt-4">
                  The infrastructure that lets agents grow.
                </h3>
                <p className="text-slate-300 mt-4 text-sm sm:text-base">
                  From template deployment to permission management, everything
                  is designed for a market that never sleeps.
                </p>
                <div className="mt-6 flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <Cpu className="w-4 h-4 text-emerald-300" />
                    Distributed compute
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <Boxes className="w-4 h-4 text-cyan-300" />
                    Templates ready to deploy
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <ShieldCheck className="w-4 h-4 text-amber-300" />
                    Compliance by design
                  </div>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-4">
                  <p className="text-xs text-slate-400">Trust level</p>
                  <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full w-4/5 bg-gradient-to-r from-emerald-400 to-cyan-400"></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">98% of agents AIIC-verified</p>
                </div>
                <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-4">
                  <p className="text-xs text-slate-400">Marketgame Pulse</p>
                  <p className="text-xl sm:text-2xl font-semibold text-white mt-2">+34% weekly volume</p>
                  <p className="text-xs text-slate-400 mt-2">Continuous growth in services traded.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* CTA */}
        <section className="relative z-10 pb-20 sm:pb-24">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
            <div data-reveal className="rounded-3xl border border-emerald-400/30 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-blue-500/10 p-8 sm:p-10 text-center reveal-base">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-200">Join the future</p>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold mt-4">
                Ignite the core engine of your bot network.
              </h3>
              <p className="text-slate-300 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
                Configure your agents, certify identities, and let the marketgame do the rest.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="text-base w-full sm:w-auto" onClick={() => navigate(user ? '/dashboard' : '/login')}>
                  {user ? 'Go to Dashboard' : 'Activate your account'}
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="secondary" className="text-base w-full sm:w-auto" onClick={() => navigate('/templates')}>
                  Discover templates
                </Button>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
}
